import { useCallback, useState } from "react";

import { HelpCircle, X } from "lucide-react";
import type PartySocket from "partysocket";

import { Button } from "@/components/ui/button";

import { useOpenUrl } from "../../hooks/useOpenUrl";
import { DownloadAppUIWrapper } from "../Download/DownloadAppUI";
import { OrganisationKnowledgeManagerUI } from "../Knowledge/OrganisationKnowledgeManagerUI";
import { AppSettingsUI } from "./App/AppSettingsUI";
import { IntegrationsManagerUI } from "./Integrations/IntegrationsManagerUI";
import { CallScriptManagerUI } from "./Script/CallScriptManagerUI";
import { SettingsMenu } from "./SettingsMenu";

export type SettingsTab =
  | "knowledge"
  | "script"
  | "download"
  | "integrations"
  | "app";

export interface SettingsManagerUIProps {
  onClose: () => void;
  partySocket: PartySocket;
}

export const SettingsManagerUI = ({
  partySocket,
  onClose,
}: Readonly<SettingsManagerUIProps>) => {
  const [activeMode, setActiveMode] = useState<SettingsTab>("app");
  const openUrl = useOpenUrl();
  const goToHelp = useCallback(() => {
    const url = "https://intercom.help/frontier-ai/en";
    openUrl(url);
  }, [openUrl]);
  return (
    <div className="flex h-full max-h-screen flex-col overflow-hidden">
      <div className="grid w-full grid-cols-3 items-center p-2">
        <div />
        <h2 className="text-md text-center font-semibold">Settings</h2>
        <div className="flex justify-end space-x-2">
          <Button onClick={goToHelp} variant="ghost" size="icon">
            <HelpCircle className="h-6 w-6" />
          </Button>
          <Button onClick={onClose} variant="ghost" size="icon">
            <X className="h-6 w-6" />
          </Button>
        </div>
      </div>

      <div className="flex flex-grow flex-col overflow-hidden">
        <div className="flex justify-center">
          <SettingsMenu activeMode={activeMode} setActiveMode={setActiveMode} />
        </div>
        <div className="flex-grow overflow-hidden">
          {activeMode === "knowledge" && (
            <OrganisationKnowledgeManagerUI partySocket={partySocket} />
          )}
          {activeMode === "script" && (
            <CallScriptManagerUI partySocket={partySocket} />
          )}
          {activeMode === "download" && <DownloadAppUIWrapper />}
          {activeMode === "integrations" && <IntegrationsManagerUI />}
          {activeMode === "app" && <AppSettingsUI />}
        </div>
      </div>
    </div>
  );
};
